<template>
    <section class="share-section">
        <div class="row">
            <div class="col-md-6">
                <header class="share-header">
                    <div class="title-wrap">
                        <h2>Comparte tu Libro de Reclamaciones</h2>
                        <p>Comparte el enlace con tus clientes o incrústalo en tu web.</p>
                    </div>
                </header>

                <div>
                    <!-- Columna 1: Enlace + Redes -->
                    <div class="share-box animate__animated animate__fadeUp mt-3">
                        <label class="block-label">Enlace directo</label>

                        <div class="url-bar shadow-sm">
                            <i class="ri-link url-bar__icon"></i>

                            <!-- URL visible y truncada -->
                            <span class="url-bar__text small" :title="shareUrl">{{ shareUrl }}</span>

                            <!-- Acción: Copiar -->
                            <button class="button button-light px-2" @click="copy(shareUrl, 'url')"
                                :class="{ 'text-success': copied.url }" type="button" title="Copiar URL">
                                <i :class="copied.url ? 'ri-check-line' : 'ri-file-copy-line'"></i>
                            </button>

                            <!-- Acción: Abrir -->
                            <a :href="shareUrl" target="_blank" rel="noopener" class="button button-primary small text-decoration-none py-2 px-2"
                                title="Abrir en una nueva pestaña">
                                <i class="ri-external-link-line small"></i>
                                <span class="small d-none d-sm-inline ml-1">Abrir</span>
                            </a>
                        </div>

                        <div class="my-4"></div>

                        <div class="social-share">
                            <p class="block-label">Compartir en redes</p>
                            <div class="d-flex flex-wrap align-items-center">
                                <!-- SOLO ÍCONO + CIRCULARES -->
                                <ShareNetwork network="whatsapp" url="" :title="shareText"
                                    class="btn-social btn-whatsapp mr-2 mb-2" aria-label="Compartir por WhatsApp">
                                    <i class="ri-whatsapp-line"></i><span class="sr-only">WhatsApp</span>
                                </ShareNetwork>

                                <ShareNetwork network="facebook" :url="shareUrl"
                                    class="btn-social btn-facebook mr-2 mb-2" aria-label="Compartir en Facebook">
                                    <i class="ri-facebook-fill"></i><span class="sr-only">Facebook</span>
                                </ShareNetwork>

                                <ShareNetwork network="twitter" :title="shareText" hashtags="librodereclamaciones,zet"
                                    class="btn-social btn-x mr-2 mb-2" aria-label="Compartir en X">
                                    <i class="ri-twitter-x-line"></i><span class="sr-only">X</span>
                                </ShareNetwork>

                                <ShareNetwork network="linkedin" :url="shareUrl"
                                    class="btn-social btn-linkedin mr-2 mb-2" aria-label="Compartir en LinkedIn">
                                    <i class="ri-linkedin-fill"></i><span class="sr-only">LinkedIn</span>
                                </ShareNetwork>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-md-6">
                <!-- Columna 2: QR | HTML -->
                <div class="share-box animate__animated animate__fadeUp delay mt-5 mt-md-0">
                    <div class="tabs d-flex justify-content-center">
                        <button :class="['btn btn-primary', panel === 'qr' && 'active']" @click="switchPanel('qr')">
                            <i class="ri-qr-code-line mr-2"></i><span class="small">QR</span>
                        </button>
                        <button :class="['btn btn-primary', panel === 'html' && 'active']" @click="switchPanel('html')">
                            <i class="ri-code-line mr-2"></i><span class="small">HTML</span>
                        </button>
                    </div>

                    <!-- Panel QR (sin controles) -->
                    <div class="d-flex flex-column justify-content-center align-items-center animate__animated animate__fadeIn"
                        v-if="panel === 'qr'">
                        <div>
                            <label class="block-label mt-2 pl-md-2 mb-0 pb-0">Comparte como código QR</label>
                            <canvas ref="qrCanvas" :width="qrSize" :height="qrSize" class="qr-canvas"></canvas>
                        </div>

                        <div>
                            <button class="button button-light" @click="downloadQR">
                                <i class="ri-download-2-line mr-2"></i><span class="small font-weight-bold">Descargar PNG</span>
                            </button>
                            <button class="button button-light" @click="printQR">
                                <i class="ri-printer-line mr-2"></i><span class="small font-weight-bold">Imprimir</span>
                            </button>
                        </div>
                    </div>

                    <!-- Panel HTML -->
                    <div class="d-flex flex-column justify-content-center animate__animated animate__fadeIn" v-else>
                        <label class="block-label mt-2">Código HTML para incrustar</label>
                        <div class="code-box">
                            <pre class="code pt-5"><code ref="code">{{ embedCode }}</code></pre>
                            <button class="button button-light py-2 px-3 copy-chip" @click="copy(embedCode, 'html')">
                                <i class="mr-2"
                                    :class="copied.html ? 'ri-check-line text-success' : 'ri-file-copy-line'"></i><span>{{
                                        copied.html ? '¡Copiado!' : 'Copiar HTML'
                                    }}</span>
                            </button>
                        </div>
                        <p class="small text-muted d-inline-block mt-2">Pega este código en tu web.</p>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import QRCode from 'qrcode'

import { mapGetters } from 'vuex'

export default {
    name: 'ShareCompanyLink',
    data() {
        return {
            webUrl: process.env.VUE_APP_WEB_URL,
            panel: 'qr',
            copied: { url: false, html: false },
            qrSize: 240,
            qrMargin: 2,
            qrEC: 'H' // High por defecto
        }
    },
    computed: {
        companyName() {
            return (this.business && this.business.tradeName) ? this.business.tradeName : ''
        },
        ...mapGetters({
            business: "app/getBusiness",
        }),
        shareUrl() { return `${this.webUrl}/${this.business.slug}` },
        shareText() {
            return `Estimado cliente,
Compartimos con usted nuestro Libro de Reclamaciones Virtual, donde podrá registrar cualquier reclamo o queja relacionada con nuestros productos o servicios.

Acceda aquí: ${this.shareUrl}

Agradecemos su confianza y estamos comprometidos con brindarle la mejor atención.
            `
        },
        embedCode() {
            return `<a href="${this.shareUrl}" target="_blank" rel="noopener noreferrer">
    <img src="https://wize.pe/wp-content/uploads/elementor/thumbs/libro-de-reclamanciones-r72mib8y6u4dg8aio881rg960nfcc9a00y848jhyum.png" />
</a>`
        }
    },
    watch: {
        shareUrl() { this.renderQR() }
    },
    mounted() { this.renderQR() },
    methods: {
        switchPanel(k) {
            this.panel = k
            this.renderQR()
        },
        async renderQR() {
            setTimeout(async () => {
                const canvas = this.$refs.qrCanvas
                if (!canvas) return
                try {
                    await QRCode.toCanvas(canvas, this.shareUrl, {
                        width: this.qrSize,
                        margin: this.qrMargin,
                        errorCorrectionLevel: this.qrEC
                    })
                } catch (err) {
                    console.error(err)
                }
            }, 500)
        },
        downloadQR() {
            const canvas = /** @type {HTMLCanvasElement} */ (this.$refs.qrCanvas)
            const link = document.createElement('a')
            link.download = `qr-${this.companyName}.png`
            link.href = canvas.toDataURL('image/png')
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        async printQR() {
            // === Texto completo (intro + pasos) definido aquí ===
            const title = 'Libro de Reclamaciones Virtual'
            const tmplMsg = `Estimado cliente,
Compartimos con usted nuestro Libro de Reclamaciones Virtual, donde podrá registrar cualquier reclamo o queja relacionada con nuestros productos o servicios.

1- Abra su navegador de preferencia (Chrome, Safari, Edge u otro).
2- Busque y seleccione la opción "Escanear código QR".
3- Apunte su teléfono hacia el código QR.
4- Presione el enlace que se mostrará en pantalla para ingresar al formulario.`

            // Opcionales si ya los usas:
            const logoUrl = (this.business && this.business.logo) ? this.business.logo.url : ''
            const sizeMm = 80

            // 1) Separar intro / pasos / (no usamos cierre desde tmplMsg)
            const urlReal = this.shareUrl
            const messageRaw = tmplMsg.replace(/\{\{\s*url\s*\}\}/gi, urlReal)

            // Soporta "1- ", "1) ", "1. " y verbos clave; SIN escapes innecesarios
            const isStep = (s) => {
                const t = s.trim()
                return /^\s*(\d+[.)-])?\s*(Abra|Busque|Seleccione|Apunte|Presione|Toque|Escanee)\b/i.test(t)
            }

            const lines = messageRaw.split(/\r?\n/)
            const introTop = []
            const steps = []
            let mode = 'introTop'

            for (const line of lines) {
                const t = line.trim()
                if (mode === 'introTop') {
                    if (t && isStep(t)) { mode = 'steps'; steps.push(line) }
                    else { introTop.push(line) }
                } else {
                    if (t && isStep(t)) steps.push(line)
                }
            }

            // 2) Escape de HTML y negrita de URL (si aparece en el texto)
            const esc = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            const escUrl = esc(urlReal)
            const urlRegexEsc = new RegExp(escUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')
            const toHtmlWithBoldUrl = (txt) => esc(txt).replace(urlRegexEsc, `<strong>${escUrl}</strong>`)

            const introTopText = introTop.join('\n').replace(/\n{3,}/g, '\n\n').trim()
            const introTopHtml = introTopText ? toHtmlWithBoldUrl(introTopText) : ''

            // Quita numeración "1- | 1) | 1." antes de meter en <ol>
            const stepsHtml = steps
                .map(s => s.replace(/^\s*\d+[.)-]\s*/, ''))
                .map(s => `<li>${esc(s)}</li>`)
                .join('')

            // Mensaje requerido DEBAJO de la lista (URL en negrita)
            const troubleNoteHtml =
                `<p class="msg">Si tiene inconvenientes, también puede ingresar directamente a: <strong>${escUrl}</strong></p>`

            // 3) Generar QR de alta resolución (300 dpi)
            const dpi = 300
            const mmToPx = mm => Math.round((mm / 25.4) * dpi)
            const printPx = mmToPx(sizeMm)

            const tmp = document.createElement('canvas')
            tmp.width = printPx
            tmp.height = printPx

            const ecLevel = this.qrEC || 'H'
            const margin = typeof this.qrMargin === 'number' ? this.qrMargin : 2

            try {
                await QRCode.toCanvas(tmp, urlReal, {
                    width: printPx,
                    margin,
                    errorCorrectionLevel: ecLevel
                })
            } catch (err) {
                if (process.env.NODE_ENV !== 'production') console.debug('QR gen error:', err)
                return
            }

            const dataUrl = tmp.toDataURL('image/png')

            // 4) Imprimir en iframe oculto (sin pop-up)
            const iframe = document.createElement('iframe')
            Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' })
            document.body.appendChild(iframe)

            const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>${esc(this.companyName || '')}</title>
  <style>
    @page { margin: 12mm; size: A4; }
    html, body { height: 100%; }
    body {
      margin: 0;
      font: 13pt/1.45 system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      color: #111;
      background: #fff;
      display: grid;
      place-items: center;
    }
    .wrap { max-width: 180mm; width: 100%; text-align: center; }
    .logo {
      max-height: 24mm; margin: 0 auto 8mm; object-fit: contain;
      display: ${logoUrl ? 'block' : 'none'};
    }
    h1 { font-size: 18pt; margin: 0 0 4mm; font-weight: 700; }
    .msg { white-space: pre-wrap; text-align: center; color: #444; margin: 0 0 8mm; }
    .steps { text-align: left; margin: 0 auto 8mm; padding-left: 6mm; max-width: 150mm; color: #333; }
    .steps li { margin: 2mm 0; }
    .qr  { width: ${sizeMm}mm; height: auto; display: block; margin: 0 auto 4mm; }
    .url { color: #444; font-size: 11pt; }
  </style>
</head>
<body>
  <div class="wrap">
    <img id="logo" class="logo" src="${logoUrl}" alt="" />
    <h1>${esc(title)}</h1>
    ${introTopHtml ? `<p class="msg">${introTopHtml}</p>` : ``}
    ${steps.length ? `<ol class="steps">${stepsHtml}</ol>` : ``}
    ${troubleNoteHtml}
    <img id="qr" class="qr" src="${dataUrl}" alt="" />
    <div class="url"><strong>${escUrl}</strong></div>
  </div>
</body>
</html>`

            const doc = iframe.contentDocument || iframe.contentWindow.document
            doc.open(); doc.write(html); doc.close()

            // 5) Esperar imágenes y lanzar impresión
            const imgs = []
            const qrImg = doc.getElementById('qr')
            const logoImg = doc.getElementById('logo')
            if (qrImg) imgs.push(qrImg)
            if (logoUrl && logoImg) imgs.push(logoImg)

            let pending = imgs.length || 1
            const done = () => {
                if (--pending <= 0) {
                    setTimeout(() => {
                        const w = iframe.contentWindow
                        w && w.focus()
                        w && w.print()
                        setTimeout(() => document.body.removeChild(iframe), 250)
                    }, 50)
                }
            }

            imgs.forEach(img => {
                if (img.complete) return done()
                img.onload = done
                img.onerror = done
            })
            if (!imgs.length) done()
        },
        async copy(value, key) {
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(value)
                } else {
                    const el = document.createElement('textarea')
                    el.value = value
                    document.body.appendChild(el)
                    el.select()
                    document.execCommand('copy')
                    document.body.removeChild(el)
                }
                this.$set(this.copied, key, true)
                setTimeout(() => this.$set(this.copied, key, false), 1200)
            } catch (err) {
                if (process.env.NODE_ENV !== 'production') console.debug('Copy error:', err)
            }
        }
    }
}
</script>

<style scoped>
@import '../Styles/share-url.styl';
</style>